import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  AddEditJobRequest,
  CalculateJobETARequest,
  EditJobRequest,
  GetJobsGridDataRequest,
  Job,
  JobDetails,
  JobFilters,
  LocationsToAddInJobs,
} from 'planning-portal-library/src/lib/core/domain';
import { DeleteJobRequest } from 'planning-portal-library/src/lib/core/domain/requests/jobs/delete-jobs-request';
import { injectEnvironment } from 'planning-portal-library/src/lib/core/environments/environment.token';
import { JobsRepository } from 'planning-portal-library/src/lib/core/repositories';
import {
  catchError,
  forkJoin,
  map,
  mergeMap,
  Observable,
  reduce,
  share,
} from 'rxjs';
import {
  BatchInjectLocationInJobRequest,
  InjectLocationInJobRequest,
  JobTemplate,
  OptimizeJobRequest,
  ValidateJobTerminalsAndLocationRequest,
  ValidateNewJobTerminalsAndLocationRequest,
} from '../../../../core/domain';
import { BaseGridResponse } from '../../../shared/base-grid.response';
import { BaseRequest } from '../../../shared/base.request';
import { BaseResponse } from '../../../shared/base.response';
import { isBlobError, parseErrorBlob } from '../../../shared/helpers';
import { LocationsApiUrls } from '../../locations/locations-web-repository/location-api-urls';
import { JobDetailsWebEntity } from './entities/job-details-web-entity';
import { JobDetailsWebRepositoryMapper } from './entities/job-details-web-entity-mapper';
import { JobFiltersWebEntity } from './entities/job-filters-web-entity';
import { JobFilterWebRepositoryMapper } from './entities/job-filters-web-entity-mapper';
import { JobTemplateWebEntity } from './entities/job-template-web-entity';
import { JobTemplateWebRepositoryMapper } from './entities/job-template-web-repository-mapper';
import { JobWebEntity } from './entities/job-web-entity';
import { JobWebRepositoryMapper } from './entities/job-web-repository-mapper';
import { LocationsToAddInJobEntity } from './entities/locations-to-add-in-job-web-entity';
import { LocationsToAddInJobWebRepositoryMapper } from './entities/locations-to-add-in-job-web-entity-mapper';
import { JobApiUrls } from './job-api-urls';
import { CalculatePlanJobETARequest } from 'planning-portal-library/src/lib/core/domain/requests/jobs/calaculate-plan-job-eta-request';
import { GetHDSequenceRequest } from 'planning-portal-library/src/lib/core/domain/requests/jobs/get-hd-sequence-request';
import { UpdateHdJobRequest } from 'planning-portal-library/src/lib/core/domain/requests/jobs/update-hd-job-request';

@Injectable({ providedIn: 'root' })
export class JobWebRepository extends JobsRepository {
  private readonly environment = injectEnvironment();
  private readonly jobMapper = new JobWebRepositoryMapper();
  private readonly jobTemplateMapper = new JobTemplateWebRepositoryMapper();
  private readonly jobFilterMapper = new JobFilterWebRepositoryMapper();
  private readonly jobDetailsMapper = new JobDetailsWebRepositoryMapper();
  private readonly locationsToAddInJobMapper =
    new LocationsToAddInJobWebRepositoryMapper();
  jobs: JobWebEntity[] = [];

  constructor(private readonly http: HttpClient) {
    super();
  }

  getJobDetails(request: BaseRequest<string>): Observable<JobDetails> {
    return this.http
      .get<BaseResponse<JobDetailsWebEntity>>(
        `${this.environment.url}${JobApiUrls.GetJobDetails}`,
        {
          params: {
            id: request.request,
          },
        }
      )
      .pipe(
        map((response: BaseResponse<JobDetailsWebEntity>) => response.result!),
        catchError((err) => {
          throw err;
        })
      )
      .pipe(map(this.jobDetailsMapper.mapFrom));
  }

  getJobs(
    request: BaseRequest<GetJobsGridDataRequest>
  ): Observable<BaseGridResponse<Job>> {
    const httpMap = this.http
      .post<BaseResponse<BaseGridResponse<JobWebEntity>>>(
        `${this.environment.url}${JobApiUrls.GetJobs}`,
        request.request
      )
      .pipe(share());

    const dataResultMap = httpMap
      .pipe(
        mergeMap((response) => response.result!.data),
        catchError((err) => {
          throw err;
        })
      )
      .pipe(map(this.jobMapper.mapFrom))
      .pipe(reduce((items: Job[], item: Job) => [...items, item], []));

    const totalRecordsResultMap = httpMap.pipe(
      map((response) => response.result!.totalRecords)
    );

    return forkJoin([dataResultMap, totalRecordsResultMap]).pipe(
      map((input) => {
        const item: BaseGridResponse<Job> = {
          data: input[0],
          totalRecords: +input[1],
        };
        return item;
      })
    );
  }
    getHDPlanJobs(request: string): Observable<Job[]> {
    return this.http
      .get<BaseResponse<JobWebEntity[]>>(
        `${this.environment.url}${JobApiUrls.GetHDPlanJobs}`,
        {
          params: {
            planId: request,
          },
        }
      )
      .pipe(
        mergeMap((response) => response.result!),
        catchError((err) => {
          throw err;
        })
      )
      .pipe(map(this.jobMapper.mapFrom))
      .pipe(reduce((items: Job[], item: Job) => [...items, item], []));
  }
  getPlanJobs(request: string): Observable<Job[]> {
    return this.http
      .get<BaseResponse<JobWebEntity[]>>(
        `${this.environment.url}${JobApiUrls.GetPlanJobs}`,
        {
          params: {
            planId: request,
          },
        }
      )
      .pipe(
        mergeMap((response) => response.result!),
        catchError((err) => {
          throw err;
        })
      )
      .pipe(map(this.jobMapper.mapFrom))
      .pipe(reduce((items: Job[], item: Job) => [...items, item], []));
  }

  CalculateHDPlanJobETA(request: CalculatePlanJobETARequest): Observable<boolean> {
      return this.http
        .post<BaseResponse<string>>(
          `${this.environment.url}${JobApiUrls.CalculateHDPlanJobETA}`,
          request
        )
        .pipe(
          map((response: any) => {
            return response.successful;
          }),
          catchError((err) => {
            throw err;
          })
        );
    }

  getJobFilters(request: string): Observable<JobFilters> {
    const httpMap = this.http.get<BaseResponse<JobFiltersWebEntity>>(
      `${this.environment.url}${JobApiUrls.GetPlanRules}`,
      {
        params: {
          planId: request,
        },
      }
    );

    return httpMap
      .pipe(
        map((response: BaseResponse<JobFiltersWebEntity>) => response.result!),
        catchError((err) => {
          throw err;
        })
      )
      .pipe(map(this.jobFilterMapper.mapFrom));
  }

  addJob(request: AddEditJobRequest): Observable<boolean> {
    return this.http
      .post<BaseResponse<boolean>>(
        `${this.environment.url}${JobApiUrls.CreateJob}`,
        request
      )
      .pipe(
        map((data: BaseResponse<boolean>) => {
          return data.successful;
        }),
        catchError((err) => {
          throw err;
        })
      );
  }

  updateJob(request: EditJobRequest): Observable<boolean> {
    return this.http
      .post<BaseResponse<boolean>>(
        `${this.environment.url}${JobApiUrls.EditJob}`,
        request
      )
      .pipe(
        map((data: BaseResponse<boolean>) => {
          return data.successful;
        }),
        catchError((err) => {
          throw err;
        })
      );
  }
  updateHdJob(request: UpdateHdJobRequest): Observable<boolean> {
    return this.http
      .post<BaseResponse<boolean>>(
        `${this.environment.url}${JobApiUrls.UpdateHdJob}`,
        request
      )
      .pipe(
        map((data: BaseResponse<boolean>) => {
          return data.successful;
        }),
        catchError((err) => {
          throw err;
        })
      );
  }


  deleteJob(request: DeleteJobRequest): Observable<boolean> {
    return this.http
      .post<BaseResponse<unknown>>(
        `${this.environment.url}${JobApiUrls.DeleteJob}`,
        request
      )
      .pipe(
        map((data) => {
          return data.successful;
        }),
        catchError((err) => {
          throw err;
        })
      );
  }

  printJob(request: string): Observable<HttpResponse<Blob>> {
    const params = new HttpParams().set('id', request);
    return this.http
      .get(`${this.environment.url}${JobApiUrls.PrintJob}`, {
        params,
        responseType: 'blob',
        observe: 'response',
      })
      .pipe(
        catchError((err) => {
          if (isBlobError(err)) {
            return parseErrorBlob(err);
          }
          throw err;
        })
      );
  }

  optimizeJob(request: OptimizeJobRequest): Observable<boolean> {
    return this.http
      .post<BaseResponse<unknown>>(
        `${this.environment.url}${JobApiUrls.OptimizeJob}`,
        request
      )
      .pipe(
        map((data) => {
          return data.successful;
        }),
        catchError((err) => {
          throw err;
        })
      );
  }

  calculateJobETA(request: CalculateJobETARequest): Observable<boolean> {
    return this.http
      .post<BaseResponse<unknown>>(
        `${this.environment.url}${JobApiUrls.CalculateJobETA}`,
        request
      )
      .pipe(
        map((data) => {
          return data.successful;
        }),
        catchError((err) => {
          throw err;
        })
      );
  }

  getLocationsToAddInJob(request: string): Observable<LocationsToAddInJobs[]> {
    return this.http
      .get<BaseResponse<LocationsToAddInJobEntity[]>>(
        `${this.environment.url}${LocationsApiUrls.GetDistinctPlanLocations}`,
        {
          params: {
            planId: request,
          },
        }
      )
      .pipe(
        mergeMap(
          (response: BaseResponse<LocationsToAddInJobEntity[]>) =>
            response.result!
        )
      )
      .pipe(map(this.locationsToAddInJobMapper.mapFrom))
      .pipe(
        reduce(
          (
            items: LocationsToAddInJobEntity[],
            item: LocationsToAddInJobEntity
          ) => [...items, item],
          []
        )
      );
  }
  injectLocationInJobs(
    request: InjectLocationInJobRequest
  ): Observable<boolean> {
    return this.http
      .post<BaseResponse<unknown>>(
        `${this.environment.url}${JobApiUrls.InjectLocationInJobs}`,
        request
      )
      .pipe(
        map((data) => {
          return data.successful;
        }),
        catchError((err) => {
          throw err;
        })
      );
  }

  batchInjectLocationInJobs(
    request: BatchInjectLocationInJobRequest
  ): Observable<boolean> {
    return this.http
      .post<BaseResponse<unknown>>(
        `${this.environment.url}${JobApiUrls.BatchInjectLocationInJobs}`,
        request
      )
      .pipe(
        map((data) => {
          return data.successful;
        }),
        catchError((err) => {
          throw err;
        })
      );
  }
  getJobTemplateLookUp(): Observable<JobTemplate[]> {
    return this.http
      .get<BaseResponse<JobTemplateWebEntity[]>>(
        `${this.environment.url}${JobApiUrls.GetJobTemplateLookUp}`
      )
      .pipe(
        mergeMap(
          (response: BaseResponse<JobTemplateWebEntity[]>) => response.result!
        ),
        catchError((err) => {
          throw err;
        })
      )
      .pipe(map(this.jobTemplateMapper.mapFrom))
      .pipe(
        reduce(
          (items: JobTemplateWebEntity[], item: JobTemplateWebEntity) => [
            ...items,
            item,
          ],
          []
        )
      );
  }
  JobTerminalsAndLocationsCheck(
    request: ValidateJobTerminalsAndLocationRequest
  ): Observable<boolean> {
    return this.http
      .post<BaseResponse<unknown>>(
        `${this.environment.url}${JobApiUrls.JobTerminalsAndLocationsCheck}`,
        request
      )
      .pipe(
        map((data) => {
          return data.successful;
        }),
        catchError((err) => {
          throw err;
        })
      );
  }

  NewJobTerminalsAndLocationsCheck(
    request: ValidateNewJobTerminalsAndLocationRequest
  ): Observable<boolean> {
    return this.http
      .get<BaseResponse<boolean>>(
        `${this.environment.url}${JobApiUrls.JobNewTerminalsAndLocationsCheck}`,
        {
          params: {
            terminalName: request.terminalName,
            jobId: request.jobId,
          },
        }
      )
      .pipe(
        map((data) => {
          return data.successful;
        }),
        catchError((err) => {
          throw err;
        })
      );
  }

  GetHDSequence(request: GetHDSequenceRequest): Observable<boolean> {
      return this.http
        .post<BaseResponse<string>>(
          `${this.environment.url}${JobApiUrls.GetHDSequence}`,
          request
        )
        .pipe(
          map((response: any) => {
            return response.successful;
          }),
          catchError((err) => {
            throw err;
          })
        );
  }

}
